import { createRef, PureComponent } from 'react';

import Cropper from 'my-elements/Cropper';
import LoadingIndicator from 'my-elements/LoadingIndicator';

import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const NUMBER_DISPLAY_VARIANTS = 6;

export default class ImageControls extends PureComponent {
  state = { cropped: false, startIndex: 0 };
  cropper = createRef();

  handleSave = () => {
    const { cropped, croppedFilename, updatedFilename } = this.state;
    const {
      attachment,
      attachment: { filename, id, original = {} },
      onCrop,
      onUpdate,
    } = this.props;

    const defaultCropFilename = `${filename.split('.')[0]}-crop-${Date.now()}`;

    if (cropped) {
      this.cropper.current.getCroppedCanvas().then(croppedCanvas => {
        onCrop(croppedCanvas, croppedFilename || defaultCropFilename, original.id || id);
      });
    } else if (updatedFilename) {
      onUpdate(attachment, { filename: updatedFilename });
    } else {
      onUpdate(attachment);
    }
  };

  loadMoreVariantsRight = numOfVariants => {
    const { startIndex } = this.state;
    const newIndex = startIndex + NUMBER_DISPLAY_VARIANTS;
    if (newIndex >= numOfVariants) this.setState({ startIndex: 0 });
    else this.setState({ startIndex: newIndex });
  };

  loadMoreVariantsLeft = numOfVariants => {
    const { startIndex } = this.state;
    const newIndex = startIndex - NUMBER_DISPLAY_VARIANTS;
    if (newIndex >= 0) this.setState({ startIndex: newIndex });
    else if (!(numOfVariants % NUMBER_DISPLAY_VARIANTS)) {
      this.setState({ startIndex: numOfVariants - NUMBER_DISPLAY_VARIANTS });
    } else {
      this.setState({ startIndex: Math.floor(numOfVariants / NUMBER_DISPLAY_VARIANTS) * NUMBER_DISPLAY_VARIANTS });
    }
  };

  render() {
    const { attachment, fetchError, fetching, onClose, showDialog } = this.props;
    const { cropped } = this.state;
    const originalFilename = attachment && attachment.filename.split('.')[0];
    const defaultCropFilename = `${originalFilename}-crop-${Date.now()}`;

    return (
      <Dialog css={rootCss} fullWidth maxWidth="md" onClose={onClose} open={showDialog}>
        <DialogTitle variant="h6">Crop Image</DialogTitle>
        <DialogContent className="IC-content">
          {fetching ?
            <div className="IC-loading">
              <LoadingIndicator />
            </div>
          : fetchError ?
            <DialogContentText>
              {[401, 403].includes(fetchError.status) ?
                'You can only crop pictures you uploaded. Upload your own copy to crop it.'
              : "Couldn't load this picture. Close this and try again."}
            </DialogContentText>
          : <div className="IC-imageContent">
              <div>
                <DialogContentText sx={{ mb: 1 }} variant="subtitle2">
                  Current Image
                </DialogContentText>
                <Cropper
                  autoCropArea={0.5}
                  className="IC-imageContainer"
                  onCropStart={() => this.setState({ cropped: true })}
                  ref={this.cropper}
                  src={attachment.urls.original}
                />
                <TextField
                  className="IC-textfield"
                  defaultValue={originalFilename}
                  disabled={cropped}
                  label="Rename File"
                  onChange={e => this.setState({ updatedFilename: e.target.value })}
                  variant="standard"
                />
              </div>

              <div>
                <DialogContentText sx={{ mb: 1 }} variant="subtitle2">
                  Preview
                </DialogContentText>
                {!cropped && <DialogContentText variant="body2">Start Cropping to view Preview</DialogContentText>}
                <cropper-viewer
                  class="IC-previewContainer"
                  selection="#cropperSelection"
                  style={{ visibility: cropped ? 'visible' : 'hidden' }}
                ></cropper-viewer>
                <TextField
                  className="IC-textfield"
                  defaultValue={defaultCropFilename}
                  label="Cropped File Name"
                  onChange={e => this.setState({ croppedFilename: e.target.value })}
                  style={{ visibility: cropped ? 'visible' : 'hidden' }}
                  variant="standard"
                />
              </div>
            </div>
          }
          {attachment && this.renderVariants()}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={onClose} variant="outlined">
            Cancel
          </Button>
          {!fetchError && (
            <Button onClick={this.handleSave} variant="outlined">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  renderVariants = () => {
    const {
      attachment: { original, variants = [] },
    } = this.props;
    const displayVariants = original ? [original, ...variants] : [...variants];

    if (!displayVariants.length) return null;

    if (displayVariants.length < NUMBER_DISPLAY_VARIANTS)
      return (
        <>
          <DialogContentText variant="subtitle2">Related Images</DialogContentText>
          <div className="IC-imagesCarousel">
            {displayVariants.map(({ id, thumb }) => this.renderVariant(id, thumb))}
          </div>
        </>
      );

    const { startIndex } = this.state;
    return (
      <>
        <DialogContentText variant="subtitle2">Related Images</DialogContentText>
        <div className="IC-carouselWrapper">
          <IconButton onClick={() => this.loadMoreVariantsLeft(displayVariants.length)} size="small">
            <FontAwesomeIcon icon={faAngleLeft} size="2x" />
          </IconButton>
          <span className="IC-imagesCarousel">
            {displayVariants
              .slice(startIndex, startIndex + NUMBER_DISPLAY_VARIANTS)
              .map(({ id, thumb }) => this.renderVariant(id, thumb))}
          </span>
          <IconButton onClick={() => this.loadMoreVariantsRight(displayVariants.length)} size="small">
            <FontAwesomeIcon icon={faAngleRight} size="2x" />
          </IconButton>
        </div>
      </>
    );
  };

  renderVariant = (id, url) => {
    const { onRevert } = this.props;
    return (
      <ButtonBase key={id} className="IC-carouselItemWrapper" onClick={() => onRevert(id)}>
        <img className="IC-carouselItem" src={url} />
      </ButtonBase>
    );
  };
}

const rootCss = ({ breakpoints, spacing }) => ({
  '.IC-content': { display: 'grid', gridGap: spacing(1) },
  '.IC-loading': { height: 400 },
  '.IC-imageContent': {
    display: 'grid',
    gridTemplateColumns: '500px 300px',
    gap: spacing(2),
    padding: spacing(1),
  },
  '.IC-imageContainer': {
    display: 'block',
    width: 500,
    height: 300,
  },
  '.IC-previewContainer': {
    width: '100%',
    height: 'auto',
    overflow: 'hidden',
  },
  '.IC-textfield': { marginTop: spacing(1), width: '80%' },
  '.IC-carouselWrapper': {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  '.IC-imagesCarousel': {
    display: 'inline-grid',
    gridTemplateColumns: `repeat(${NUMBER_DISPLAY_VARIANTS}, 100px)`,
    gridColumnGap: spacing(2),
  },
  '.IC-carouselItemWrapper': {
    flexDirection: 'column',
    '&:hover': { '& .IC-carouselItem': { opacity: 1 } },
  },
  '.IC-carouselItem': { opacity: 0.5, width: 100, height: 100 },
  [breakpoints.down('lg')]: {
    '.IC-imageContent': { gridTemplateColumns: '400px, 200px' },
    '.IC-imageContainer': { width: 400, height: 300 },
    '.IC-previewContainer': { width: 200, height: 200 },
  },
});
